/**
 * DocumentPickerModal — canonical modal for selecting a document from the library.
 *
 * Drop-in for any "pick a document" use-case: brief attachment, evidence selection, etc.
 * Renders a searchable list of documents scoped by optional `queryParams`.
 *
 * Usage:
 *   <DocumentPickerModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     onSelect={doc => console.log(doc)}
 *   />
 */

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useInfiniteDocuments } from '@/services/folders/queries'
import type { DocumentInterface } from '@/services/folders/types'
import { Button } from '@/components/ui/button'
import { Eye, FileText, Loader2, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

export interface DocumentPickerModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (doc: DocumentInterface) => void
    title?: string
    description?: string
    /** Additional query params merged into the default { data_type: 'info', limit: '200' } */
    queryParams?: Record<string, string>
}

export function DocumentPickerModal({
    open,
    onOpenChange,
    onSelect,
    title = 'Select Document',
    description = 'Choose a document from your library.',
    queryParams,
}: DocumentPickerModalProps) {
    const [searchQuery, setSearchQuery] = useState('')
    const [sort, setSort] = useState<'recent' | 'oldest' | 'name_asc'>('recent')

    const mergedParams = useMemo(
        () => {
            const sortParams =
                sort === 'name_asc'
                    ? { sort: 'name', sort_by: 'asc' }
                    : sort === 'oldest'
                      ? { sort: 'updatedAt', sort_by: 'asc' }
                      : { sort: 'updatedAt', sort_by: 'desc' }
            return { data_type: 'info', ...sortParams, ...queryParams }
        },
        [queryParams, sort]
    )

    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteDocuments({
        enabled: open,
        queryParams: mergedParams,
        pageSize: 50,
    })

    const documents = useMemo(
        () => (data?.pages || []).flatMap((p: any) => (p?.data ?? []) as DocumentInterface[]).filter(d => !d.isFolder),
        [data?.pages]
    )

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return documents
        const q = searchQuery.toLowerCase()
        return documents.filter(
            d =>
                (d.name || '').toLowerCase().includes(q) ||
                (d.description || '').toLowerCase().includes(q)
        )
    }, [documents, searchQuery])

    const handleSelect = (doc: DocumentInterface) => {
        onSelect(doc)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={open => { onOpenChange(open); if (!open) setSearchQuery('') }}>
            <DialogContent className='max-w-2xl max-h-[80vh] flex flex-col gap-0 p-0 overflow-hidden'>
                <DialogHeader className='px-6 pt-5 pb-4 border-b'>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                <div className='px-6 py-3 border-b'>
                    <div className='flex items-center gap-3'>
                        <div className='relative flex-1'>
                            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                            <Input
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder='Search documents...'
                                className='pl-9'
                                autoFocus
                            />
                        </div>
                        <select
                            value={sort}
                            onChange={e => setSort(e.target.value as any)}
                            className='h-10 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700'
                            title='Sort'
                        >
                            <option value='recent'>Recent</option>
                            <option value='oldest'>Oldest</option>
                            <option value='name_asc'>Name A–Z</option>
                        </select>
                    </div>
                </div>

                <div className='flex-1 overflow-y-auto min-h-0 px-3 py-2'>
                    {isLoading ? (
                        <div className='flex items-center justify-center py-12'>
                            <Loader2 className='w-6 h-6 animate-spin text-violet-600' />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className='text-center py-12'>
                            <FileText className='w-10 h-10 text-gray-300 mx-auto mb-3' />
                            <p className='text-sm font-medium text-gray-700 mb-1'>
                                {documents.length === 0 ? 'No documents in your library' : 'No documents match your search'}
                            </p>
                            <p className='text-xs text-gray-500'>
                                {documents.length === 0
                                    ? 'Documents you create will appear here'
                                    : 'Try a different search term'}
                            </p>
                        </div>
                    ) : (
                        <div className='space-y-0.5'>
                            {filtered.map(doc => {
                                const id = (doc._id ?? doc.id)?.toString() ?? ''
                                return (
                                    <button
                                        key={id}
                                        type='button'
                                        onClick={() => handleSelect(doc)}
                                        className='w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 rounded-lg transition-colors'
                                    >
                                        <div className='w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0'>
                                            <FileText className='w-4 h-4 text-violet-600' />
                                        </div>
                                        <div className='min-w-0 flex-1'>
                                            <p className='text-sm font-medium text-gray-800 truncate'>
                                                {doc.name || 'Untitled'}
                                            </p>
                                            {doc.description && (
                                                <p className='text-xs text-gray-500 truncate mt-0.5'>{doc.description}</p>
                                            )}
                                        </div>
                                        {doc.updatedAt && (
                                            <p className='text-xs text-gray-400 shrink-0'>
                                                {new Date(doc.updatedAt).toLocaleDateString()}
                                            </p>
                                        )}
                                        {doc.url && (
                                            <button
                                                type='button'
                                                onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    window.open(doc.url, '_blank', 'noopener,noreferrer')
                                                }}
                                                className='ml-2 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 hover:bg-white hover:border-violet-200 text-gray-500 hover:text-violet-700'
                                                title='Preview'
                                            >
                                                <Eye className='w-4 h-4' />
                                            </button>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>

                {hasNextPage && (
                    <div className='px-6 py-4 border-t flex justify-center'>
                        <Button variant='outline' onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                            {isFetchingNextPage ? <Loader2 className='w-4 h-4 mr-2 animate-spin' /> : null}
                            Load more
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
